import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { PrimaryButton, SecondaryButton, ButtonContainer } from '../../atoms';
import {
  cancelApplication,
  useApplicationStore,
  UserContext
} from '../../../contexts';
import { useMutation } from '@tanstack/react-query';

type CancelApplicationProps = {
  loanApplicationOriginalId: string;
  handleModalClose: () => void;
  refreshPage: () => void;
};

const PromptText = styled.p(
  ({ theme }) => css`
    margin-left: 8px;
    margin-right: 8px;
    margin-top: 6px;
  `
);

const Wrapper = styled.div`
  text-align: center;
`;

const CancelApplication = ({
  loanApplicationOriginalId,
  handleModalClose,
  refreshPage
}: CancelApplicationProps) => {
  const [isPromptOpen, togglePromptOpen] = useState(false);

  const { user } = useContext(UserContext);
  const merchantId = user.merchantId;

  const cancelLoading = useApplicationStore((s) => s.isCancelLoading);
  const setCancelLoading = useApplicationStore((s) => s.setCancelLoading);
  const cancelApplicationMutation = cancelApplication({
    applicationId: loanApplicationOriginalId,
    merchantId,
    setCancelLoading,
    mutate: useMutation
  });

  return (
    <Wrapper>
      {!isPromptOpen && (
        <SecondaryButton
          disabled={isPromptOpen}
          onClick={() => togglePromptOpen(true)}
        >
          Cancel Application
        </SecondaryButton>
      )}

      {isPromptOpen && (
        <>
          <ButtonContainer
            horizontalSpacing="8px"
            verticalSpacing="8px"
            justify="center"
          >
            <PromptText>
              <strong>Are you sure you want to cancel the application?</strong>
            </PromptText>
            <SecondaryButton
              disabled={cancelLoading}
              onClick={() => togglePromptOpen(false)}
            >
              No
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={cancelLoading}
              isLoading={cancelLoading}
              onClick={async (e) => {
                e.stopPropagation();
                await cancelApplicationMutation.mutate(undefined, {
                  // auto-dismiss and reload list on success
                  onSuccess: () => {
                    handleModalClose();
                    refreshPage();
                  }
                });
              }}
            >
              Yes
            </PrimaryButton>
          </ButtonContainer>
        </>
      )}
    </Wrapper>
  );
};

export { CancelApplication };
